'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import { deletePlannerEventLinkedData, getApiUrl } from '@/lib/api'
import {
  listPlannerEvents,
  fetchPlannerEvent,
  createPlannerEvent,
  patchPlannerEvent,
  deletePlannerEvent,
  type PlannerEventRecord,
  type PlannerEventType,
} from '@/lib/planner-events'
import { migrateUnassignedPlannerLinks } from '@/lib/migrate-planner-links'

export type PlannerEventSummary = {
  id: string
  documentId?: string
  name: string
  eventType: PlannerEventType
  createdAt: string
}

function storageKey(userId: number) {
  return `planner-active-event-${userId}`
}

/**
 * Loads the user’s planner events from Strapi, keeps an active event,
 * and fetches full detail (JSON fields) with generation guards to avoid cross-event races.
 */
export function usePlannerEvents(userId: number | undefined, token: string | null) {
  const [summaries, setSummaries] = useState<PlannerEventSummary[]>([])
  const [activeEventId, setActiveEventIdState] = useState<string | null>(null)
  const [record, setRecord] = useState<PlannerEventRecord | null>(null)
  const [listReady, setListReady] = useState(false)
  const [detailLoading, setDetailLoading] = useState(false)
  const [needsOnboarding, setNeedsOnboarding] = useState(false)

  const summariesRef = useRef(summaries)
  summariesRef.current = summaries

  const loadGenRef = useRef(0)
  const listLoadGenRef = useRef(0)
  const activeEventIdRef = useRef<string | null>(null)

  useEffect(() => {
    activeEventIdRef.current = activeEventId
  }, [activeEventId])

  const setActiveEventId = useCallback(
    (id: string | null) => {
      setActiveEventIdState(id)
      if (userId && id) {
        try {
          localStorage.setItem(storageKey(userId), id)
        } catch {
          /* ignore */
        }
      } else if (userId) {
        try {
          localStorage.removeItem(storageKey(userId))
        } catch {
          /* ignore */
        }
      }
    },
    [userId]
  )

  // Initial list + optional migration from legacy User.customEntries / timeline
  useEffect(() => {
    if (!userId || !token) {
      listLoadGenRef.current += 1
      loadGenRef.current += 1
      setSummaries([])
      setActiveEventIdState(null)
      setRecord(null)
      setListReady(false)
      setDetailLoading(false)
      setNeedsOnboarding(false)
      return
    }

    let cancelled = false
    listLoadGenRef.current += 1
    const listGen = listLoadGenRef.current
    loadGenRef.current += 1

    // Clear stale selection immediately so no screen uses another session’s event id
    // before the new list returns (logout/login or token refresh).
    setListReady(false)
    setSummaries([])
    setActiveEventIdState(null)
    setRecord(null)
    setNeedsOnboarding(false)

    ;(async () => {
      try {
        let list = await listPlannerEvents(token, userId)
        if (cancelled || listGen !== listLoadGenRef.current) return

        if (list.length === 0) {
          if (cancelled || listGen !== listLoadGenRef.current) return
          const API_URL = getApiUrl()
          const userResponse = await fetch(`${API_URL}/api/users/me?populate=*`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
          })
          if (userResponse.ok) {
            const userData = await userResponse.json()
            const userObj = userData.data || userData

            let customStr: string | undefined
            if (userObj.customEntries) {
              customStr =
                typeof userObj.customEntries === 'string'
                  ? userObj.customEntries
                  : JSON.stringify(userObj.customEntries)
            }
            let timelineStr: string | undefined
            if (userObj.timelineEntries) {
              timelineStr =
                typeof userObj.timelineEntries === 'string'
                  ? userObj.timelineEntries
                  : JSON.stringify(userObj.timelineEntries)
            }

            const hasLegacy =
              (customStr && customStr.trim().length > 0) ||
              (timelineStr && timelineStr.trim().length > 0)

            if (hasLegacy) {
              await createPlannerEvent(token, {
                userId,
                name: 'Moj događaj',
                eventType: 'svadba',
                customEntries: customStr,
                timelineEntries: timelineStr,
              })
              list = await listPlannerEvents(token, userId)
            }
          }
        }

        if (cancelled || listGen !== listLoadGenRef.current) return

        const mapped: PlannerEventSummary[] = list.map((r) => ({
          id: String(r.id),
          documentId: r.documentId,
          name: r.name,
          eventType: r.eventType,
          createdAt: r.createdAt || new Date().toISOString(),
        }))

        if (cancelled || listGen !== listLoadGenRef.current) return

        setSummaries(mapped)

        if (mapped.length > 0) {
          try {
            await migrateUnassignedPlannerLinks(
              token,
              userId,
              Number(mapped[0].id)
            )
          } catch (migrateErr) {
            console.warn('Planner link migration:', migrateErr)
          }
        }

        if (cancelled || listGen !== listLoadGenRef.current) return

        const stored = localStorage.getItem(storageKey(userId))
        if (stored && mapped.some((e) => e.id === stored)) {
          setActiveEventIdState(stored)
        } else if (mapped.length > 0) {
          setActiveEventIdState(mapped[0].id)
          try {
            localStorage.setItem(storageKey(userId), mapped[0].id)
          } catch {
            /* ignore */
          }
        } else {
          setActiveEventIdState(null)
          setNeedsOnboarding(true)
        }
      } catch (e) {
        console.error('usePlannerEvents list', e)
        if (!cancelled && listGen === listLoadGenRef.current) {
          setSummaries([])
          setActiveEventIdState(null)
          setNeedsOnboarding(true)
        }
      } finally {
        if (!cancelled && listGen === listLoadGenRef.current) setListReady(true)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [userId, token])

  // Detail fetch when active event changes
  useEffect(() => {
    if (!userId || !token || !listReady) return

    if (!activeEventId) {
      setRecord(null)
      setDetailLoading(false)
      return
    }

    const summary = summariesRef.current.find((e) => e.id === activeEventId)
    if (!summary) {
      setRecord(null)
      setDetailLoading(false)
      return
    }

    setRecord(null)
    loadGenRef.current += 1
    const gen = loadGenRef.current

    ;(async () => {
      try {
        setDetailLoading(true)
        const r = await fetchPlannerEvent(
          token,
          Number(summary.id),
          summary.documentId
        )
        if (gen !== loadGenRef.current) return
        setRecord(r)
      } catch (e) {
        console.error('usePlannerEvents detail', e)
        if (gen === loadGenRef.current) setRecord(null)
      } finally {
        if (gen === loadGenRef.current) setDetailLoading(false)
      }
    })()
  }, [userId, token, listReady, activeEventId])

  const createEvent = useCallback(
    async (name: string, eventType: PlannerEventType) => {
      if (!userId || !token) return
      const created = await createPlannerEvent(token, {
        userId,
        name: name.trim(),
        eventType,
      })
      const sum: PlannerEventSummary = {
        id: String(created.id),
        documentId: created.documentId,
        name: created.name,
        eventType: created.eventType,
        createdAt: created.createdAt || new Date().toISOString(),
      }
      setSummaries((prev) => [...prev, sum])
      setActiveEventId(sum.id)
      setNeedsOnboarding(false)
    },
    [userId, token, setActiveEventId]
  )

  /** DELETE a planner event and fix active selection + summaries. */
  const deleteEvent = useCallback(
    async (forEventId: string) => {
      if (!token || !userId) return
      const s = summariesRef.current.find((x) => x.id === forEventId)
      if (!s) return
      const numericId = Number(s.id)
      await deletePlannerEventLinkedData(token, userId, numericId)
      await deletePlannerEvent(token, numericId, s.documentId)
      const prev = summariesRef.current
      const next = prev.filter((x) => x.id !== forEventId)
      const wasActive = activeEventIdRef.current === forEventId
      setSummaries(next)
      if (wasActive) {
        if (next.length > 0) {
          setActiveEventId(next[0].id)
        } else {
          setActiveEventId(null)
          setNeedsOnboarding(true)
          setRecord(null)
        }
      }
    },
    [token, userId, setActiveEventId]
  )

  /** PATCH the active Strapi row; pass `forEventId` from the action start to avoid wrong writes after switching. */
  const patchEvent = useCallback(
    async (
      forEventId: string,
      data: Parameters<typeof patchPlannerEvent>[3]
    ) => {
      if (!token) return
      const s = summariesRef.current.find((x) => x.id === forEventId)
      if (!s) return
      const updated = await patchPlannerEvent(
        token,
        Number(s.id),
        s.documentId,
        data
      )
      setSummaries((prev) =>
        prev.map((x) =>
          x.id === forEventId
            ? {
                ...x,
                name: updated.name,
                eventType: updated.eventType,
              }
            : x
        )
      )
      if (activeEventIdRef.current !== forEventId) return
      setRecord(updated)
    },
    [token]
  )

  /** Re-fetch detail for the active event after server-side updates. */
  const refreshActiveDetail = useCallback(async () => {
    if (!token || !activeEventId) return
    const summary = summariesRef.current.find((e) => e.id === activeEventId)
    if (!summary) return
    loadGenRef.current += 1
    const gen = loadGenRef.current
    try {
      setDetailLoading(true)
      const r = await fetchPlannerEvent(
        token,
        Number(summary.id),
        summary.documentId
      )
      if (gen !== loadGenRef.current) return
      setRecord(r)
    } catch (e) {
      console.error('refreshActiveDetail', e)
    } finally {
      if (gen === loadGenRef.current) setDetailLoading(false)
    }
  }, [token, activeEventId])

  const activeSummary =
    summaries.find((e) => e.id === activeEventId) ?? null

  const activeEventNumericId = activeSummary
    ? Number(activeSummary.id)
    : null

  return {
    summaries,
    activeEventId,
    activeEventNumericId,
    setActiveEventId,
    activeSummary,
    record,
    listReady,
    detailLoading,
    needsOnboarding,
    setNeedsOnboarding,
    createEvent,
    deleteEvent,
    patchEvent,
    refreshActiveDetail,
  }
}
